import React from "react";
import { Container, Row, Col } from "reactstrap";
import { motion } from "framer-motion";

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "Sign Up and Log In",
      description:
        "Begin your reading journey by creating an account. Once registered, our onboarding team will reach out to cleanly guide you through your dashboard setup.",
    },
    {
      number: "02",
      title: "Browse Our Catalog",
      description:
        "Explore thousands of curated titles across premium fiction, non-fiction, academic texts, regional deep-cuts, and trending global releases.",
    },
    {
      number: "03",
      title: "Build Your Virtual Shelf",
      description:
        "Select the high-quality physical books that pique your current interest and stack them cleanly onto your interactive personalized list.",
    },
    {
      number: "04",
      title: "We Curate & Prepare",
      description:
        "Our dedicated fulfillment team sanitizes, inspects, and pairs your requested volumes to guarantee a beautiful physical reading experience.",
    },
    {
      number: "05",
      title: "Doorstep Delivery",
      description:
        "Sit back and relax as our reliable ecosystem transit agents handle precise distribution directly to your designated neighborhood location.",
    },
    {
      number: "06",
      title: "Immerse & Enjoy",
      description:
        "Unwrap your clean collection and lose yourself in captivating storytelling with absolute flexibility to switch books whenever you want.",
    },
  ];

  return (
    <section className="modern-process-timeline py-5 position-relative">
      {/* Containerized Wix/Hostinger structural overrides */}
      <style>{`
        :root {
          --slate-900: #0f172a;
          --slate-500: #64748b;
          --brand-primary: #6366f1;
          --line-color: rgba(99, 102, 241, 0.12);
          --card-radius: 20px;
          --smooth-curve: cubic-bezier(0.16, 1, 0.3, 1);
        }

        .process-badge {
          display: inline-block;
          padding: 6px 14px;
          background: rgba(99, 102, 241, 0.06);
          color: var(--brand-primary);
          font-weight: 700;
          font-size: 0.85rem;
          border-radius: 100px;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .process-main-title {
          font-weight: 800;
          color: var(--slate-900);
          letter-spacing: -1.5px;
        }

        .process-grid-container {
          position: relative;
        }

        /* Desktop Process Flow-line architecture */
        @media (min-width: 992px) {
          .process-grid-container::before {
            content: '';
            position: absolute;
            top: 18%;
            left: 5%;
            right: 5%;
            height: 2px;
            background: repeating-linear-gradient(90deg, var(--line-color), var(--line-color) 8px, transparent 8px, transparent 16px);
            z-index: 1;
          }
          .process-grid-container-row-2::before {
            top: 68% !important;
          }
        }

        .modern-step-node {
          position: relative;
          z-index: 2;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.04);
          border-radius: var(--card-radius);
          padding: 32px 24px;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.01);
          transition: all 0.4s var(--smooth-curve);
        }

        .modern-step-node:hover {
          transform: translateY(-5px);
          border-color: rgba(99, 102, 241, 0.2);
          box-shadow: 0 20px 40px rgba(99, 102, 241, 0.06);
        }

        .step-num-ring {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          background: rgba(99, 102, 241, 0.06);
          color: var(--brand-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 1.1rem;
          margin-bottom: 24px;
          transition: all 0.4s var(--smooth-curve);
        }

        .modern-step-node:hover .step-num-ring {
          background: var(--brand-primary);
          color: #ffffff;
          box-shadow: 0 8px 20px rgba(99, 102, 241, 0.2);
        }

        .step-node-title {
          font-weight: 700;
          color: var(--slate-900);
          font-size: 1.2rem;
          margin-bottom: 12px;
          letter-spacing: -0.3px;
        }

        .step-node-desc {
          color: var(--slate-500);
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 0;
        }
      `}</style>

      {/* Header Block */}
      <Container className="mb-5 text-center">
        <Row className="justify-content-center">
          <Col lg="8">
            <span className="process-badge">Seamless Onboarding</span>
            <h2 className="process-main-title h1 mb-3">How It Works</h2>
            <p className="text-muted fs-5">
              We make reading completely frictionless, automated, and hyper-affordable for modern readers. Embark on your next physical literary excursion in minutes.
            </p>
          </Col>
        </Row>
      </Container>

      {/* Sequential Process Map - Split clean across two responsive rows */}
      <Container>
        {/* Row 1 (Steps 1-3) */}
        <div className="process-grid-container mb-lg-5">
          <Row className="gy-4 justify-content-center">
            {steps.slice(0, 3).map((step, index) => (
              <Col lg="4" md="6" key={step.number} className="px-3">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="modern-step-node h-100"
                >
                  <div className="step-num-ring">{step.number}</div>
                  <h3 className="step-node-title">{step.title}</h3>
                  <p className="step-node-desc">{step.description}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Row 2 (Steps 4-6) */}
        <div className="process-grid-container process-grid-container-row-2 mt-4 mt-lg-0">
          <Row className="gy-4 justify-content-center">
            {steps.slice(3, 6).map((step, index) => (
              <Col lg="4" md="6" key={step.number} className="px-3">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (index + 3) * 0.1 }}
                  className="modern-step-node h-100"
                >
                  <div className="step-num-ring">{step.number}</div>
                  <h3 className="step-node-title">{step.title}</h3>
                  <p className="step-node-desc">{step.description}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </div>
      </Container>
    </section>
  );
};

export default HowItWorks;